import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { colors } from '@/theme/tokens';
import { AppLayout, AppShell, AppSkeleton, Box, CircularProgress } from '@/components';
import { NudgeGateProvider } from '@/components/NudgeGateProvider';
import {
  RequireAuth,
  RequireCapability,
  useAuthSync,
  useUnauthorizedHandler,
  CallbackPage,
} from '@/auth';
import { useBusinessStore } from '@/stores';
// ReportsPage uses recharts which has a large dependency graph. Importing it
// eagerly avoids Vite's on-demand optimization kicking in at runtime for the
// first lazy import, which can produce "Failed to fetch dynamically imported
// module" errors during development.
import { ReportsPage } from '@/pages/admin/ReportsPage';

// Loaded only in dev; Vite tree-shakes this in production via the DEV guard below.
const LazyMockupRoutes = import.meta.env.DEV ? lazy(() => import('@/mockups/MockupRoutes')) : null;

const LazyBookingPage = lazy(() =>
  import('@/pages/public/BookingPage').then((m) => ({ default: m.BookingPage }))
);
const LazyManageAppointmentPage = lazy(() =>
  import('@/pages/public/ManageAppointmentPage').then((m) => ({ default: m.ManageAppointmentPage }))
);
const LazyEmployeeInvitePage = lazy(() =>
  import('@/pages/admin/EmployeeInvitePage').then((m) => ({ default: m.EmployeeInvitePage }))
);
const LazyBridgePage = lazy(() =>
  import('@/pages/public/BridgePage').then((m) => ({ default: m.BridgePage }))
);
const LazySetupRolePage = lazy(() =>
  import('@/pages/admin/SetupRolePage').then((m) => ({ default: m.SetupRolePage }))
);
const LazySetupWizardPage = lazy(() =>
  import('@/pages/admin/SetupWizardPage').then((m) => ({ default: m.SetupWizardPage }))
);
const LazyProviderSetupPage = lazy(() =>
  import('@/pages/admin/ProviderSetupPage').then((m) => ({ default: m.ProviderSetupPage }))
);
const LazyDashboardPage = lazy(() =>
  import('@/pages/admin/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const LazyCalendarPage = lazy(() =>
  import('@/pages/admin/CalendarPage').then((m) => ({ default: m.CalendarPage }))
);
const LazyClientsPage = lazy(() =>
  import('@/pages/admin/ClientsPage').then((m) => ({ default: m.ClientsPage }))
);
const LazyClientDetailPage = lazy(() =>
  import('@/pages/admin/ClientDetailPage').then((m) => ({ default: m.ClientDetailPage }))
);
const LazyMessagesPage = lazy(() =>
  import('@/pages/admin/MessagesPage').then((m) => ({ default: m.MessagesPage }))
);
const LazySuggestionsPage = lazy(() =>
  import('@/pages/admin/SuggestionsPage').then((m) => ({ default: m.SuggestionsPage }))
);
const LazyProvidersPage = lazy(() =>
  import('@/pages/admin/ProvidersPage').then((m) => ({ default: m.ProvidersPage }))
);
const LazyProfilePage = lazy(() =>
  import('@/pages/admin/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const LazySchedulePage = lazy(() =>
  import('@/pages/admin/SchedulePage').then((m) => ({ default: m.SchedulePage }))
);
const LazySettingsPage = lazy(() =>
  import('@/pages/admin/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const LazyBookingPageEditor = lazy(() =>
  import('@/pages/admin/booking-page/BookingPageEditor').then((m) => ({
    default: m.BookingPageEditor,
  }))
);
const LazyServiceCatalogPage = lazy(() => import('@/pages/admin/ServiceCatalogPage'));
// ReportsPage is eagerly imported above (see comment there)
const LazyStyleguidePage = lazy(() =>
  import('@/pages/admin/StyleguidePage').then((m) => ({ default: m.StyleguidePage }))
);
const LazyProviderPerformancePage = lazy(() =>
  import('@/pages/admin/ProviderPerformancePage').then((m) => ({
    default: m.ProviderPerformancePage,
  }))
);

// Lazy-loaded landing page — keeps the landing bundle separate from the app bundle.
const LazyLandingPage = lazy(() =>
  import('@/landing/LandingPage').then((m) => ({ default: m.LandingPage }))
);

// Lazy-loaded experiment landing pages — /lp/{slug}
const LazyExperimentLandingPage = lazy(() =>
  import('@/landing/experiments/ExperimentLandingPage').then((m) => ({
    default: m.RoutedExperimentLandingPage,
  }))
);

// Lazy-loaded SEO landing pages — /{slug} (salon-booking-software, etc.)
const LazySeoLandingPage = lazy(() =>
  import('@/landing/seo-pages/SeoLandingPage').then((m) => ({
    default: m.RoutedSeoLandingPage,
  }))
);

// Lazy-loaded tool pages — /tools/{slug} (formula-scaler, etc.)
const LazyToolPage = lazy(() =>
  import('@/tools/ToolPage').then((m) => ({
    default: m.RoutedToolPage,
  }))
);
const LazyToolsHubPage = lazy(() =>
  import('@/tools/ToolsHubPage').then((m) => ({
    default: m.ToolsHubPage,
  }))
);

const LazyPrivacyPage = lazy(() =>
  import('@/pages/public/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const LazyDeleteAccountPage = lazy(() =>
  import('@/pages/public/DeleteAccountPage').then((m) => ({ default: m.DeleteAccountPage }))
);
const LazySupportPage = lazy(() =>
  import('@/pages/public/SupportPage').then((m) => ({ default: m.SupportPage }))
);
const LazyAboutPage = lazy(() =>
  import('@/pages/public/AboutPage').then((m) => ({ default: m.AboutPage }))
);
const LazyNotFoundPage = lazy(() =>
  import('@/pages/public/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);

function RouteLoadingFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        bgcolor: colors.bg,
      }}
    >
      <CircularProgress sx={{ color: colors.accent }} />
    </Box>
  );
}

function AdminContentSkeleton() {
  return (
    <AppLayout>
      <Box sx={{ maxWidth: 960 }}>
        <AppSkeleton width={260} height={36} sx={{ mb: 1.5 }} />
        <AppSkeleton width={160} height={20} sx={{ mb: 5 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 3 }}>
          <AppSkeleton height={280} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <AppSkeleton height={86} />
            <AppSkeleton height={86} />
            <AppSkeleton height={160} />
          </Box>
        </Box>
      </Box>
    </AppLayout>
  );
}

function PageSuspense({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>;
}

function AdminShellPage({ synced, children }: { synced: boolean; children: ReactNode }) {
  const memberships = useBusinessStore((s) => s.memberships);
  const activeBusinessId = useBusinessStore((s) => s.activeBusinessId);

  if (!synced && memberships.length === 0) {
    return <AdminContentSkeleton />;
  }

  if (synced && memberships.length === 0) {
    return <Navigate to="/admin/setup" replace />;
  }

  // Memberships exist but every referenced business is gone (orphaned memberships).
  // Send straight to the new-business wizard so the user can recover.
  if (synced && memberships.length > 0 && !activeBusinessId) {
    return <Navigate to="/admin/setup/business" replace />;
  }

  return <PageSuspense>{children}</PageSuspense>;
}

/**
 * Default redirect:
 * - No memberships → role-selection setup flow
 * - Has any membership → dashboard (handles pending/suspended states there)
 */
function AdminDefaultRedirect({ synced }: { synced: boolean }) {
  const activeBusinessId = useBusinessStore((s) => s.activeBusinessId);
  const activeStatus = useBusinessStore((s) => s.activeStatus);
  const memberships = useBusinessStore((s) => s.memberships);

  if (!synced) {
    return <AdminContentSkeleton />;
  }

  if (memberships.length === 0) {
    return <Navigate to="setup" replace />;
  }

  if (activeBusinessId || activeStatus) {
    return <Navigate to="dashboard" replace />;
  }

  return <Navigate to="setup" replace />;
}

function AdminRoutes() {
  // Sync Auth0 user with internal user storage. `synced` flips to true once
  // the first /api/auth/sync call completes, giving us fresh memberships before
  // any route logic runs. This prevents AdminDefaultRedirect from reading stale
  // persisted memberships and bouncing the user to /admin/setup incorrectly.
  const { synced } = useAuthSync();
  // Handle 401 responses: silently refresh token or log out
  useUnauthorizedHandler();

  return (
    <Routes>
      {/* ── Standalone setup flows — no shell, full-screen ── */}
      <Route
        path="setup"
        element={
          <PageSuspense>
            <LazySetupRolePage />
          </PageSuspense>
        }
      />
      <Route
        path="setup/business"
        element={
          <PageSuspense>
            <LazySetupWizardPage />
          </PageSuspense>
        }
      />
      <Route
        path="setup/provider"
        element={
          <PageSuspense>
            <LazyProviderSetupPage />
          </PageSuspense>
        }
      />

      {/* ── Main app routes — wrapped in AppShell (top bar + sidebar) ── */}
      <Route element={<AppShell />}>
        <Route
          path="dashboard"
          element={
            <AdminShellPage synced={synced}>
              <LazyDashboardPage />
            </AdminShellPage>
          }
        />
        <Route
          path="calendar"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="appointments.view">
                <LazyCalendarPage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="my-appointments"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="appointments.view">
                <LazyCalendarPage providerScope />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="clients"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="clients.view">
                <LazyClientsPage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="clients/:id"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="clients.view">
                <LazyClientDetailPage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="messages"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="messages.view">
                <LazyMessagesPage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="messages/:clientId"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="messages.view">
                <LazyMessagesPage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="suggestions"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="messages.manage">
                <LazySuggestionsPage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="providers"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="team.manage">
                <LazyProvidersPage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="providers/:providerId/performance"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="reports.view">
                <LazyProviderPerformancePage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="profile"
          element={
            <AdminShellPage synced={synced}>
              <LazyProfilePage />
            </AdminShellPage>
          }
        />
        <Route
          path="schedule"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="schedule.self.manage">
                <LazySchedulePage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="settings"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="business.settings.manage">
                <LazySettingsPage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="booking-page"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="business.settings.manage">
                <LazyBookingPageEditor />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="catalog"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="services.manage">
                <LazyServiceCatalogPage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="reports"
          element={
            <AdminShellPage synced={synced}>
              <RequireCapability capability="reports.view">
                <ReportsPage />
              </RequireCapability>
            </AdminShellPage>
          }
        />
        <Route
          path="styleguide"
          element={
            <AdminShellPage synced={synced}>
              <LazyStyleguidePage />
            </AdminShellPage>
          }
        />
        <Route path="*" element={<AdminDefaultRedirect synced={synced} />} />
      </Route>
    </Routes>
  );
}

/** Redirects /c/:clientId → /admin/messages/:clientId for web users. */
function NavigateToChat() {
  const { clientId } = useParams<{ clientId: string }>();
  return <Navigate to={`/admin/messages/${clientId}`} replace />;
}

export function App() {
  return (
    <Routes>
      {/* Auth callback — public, no shell */}
      <Route path="/callback" element={<CallbackPage />} />

      {/* Session bridge (mobile→web handoff) — public, no shell */}
      <Route
        path="/bridge"
        element={
          <PageSuspense>
            <LazyBridgePage />
          </PageSuspense>
        }
      />

      {/* Employee invite — authenticated but minimal, no shell */}
      <Route
        path="/join/:token"
        element={
          <RequireAuth>
            <PageSuspense>
              <LazyEmployeeInvitePage />
            </PageSuspense>
          </RequireAuth>
        }
      />

      {/* Public booking page */}
      <Route
        path="/book/:slug"
        element={
          <PageSuspense>
            <LazyBookingPage />
          </PageSuspense>
        }
      />
      {/* Self-service appointment management */}
      <Route
        path="/book/:slug/manage"
        element={
          <PageSuspense>
            <LazyManageAppointmentPage />
          </PageSuspense>
        }
      />

      {/* Protected admin routes */}
      <Route
        path="/admin/*"
        element={
          <RequireAuth>
            <NudgeGateProvider>
              <AdminRoutes />
            </NudgeGateProvider>
          </RequireAuth>
        }
      />

      {/* Dev-only: isolated mockup pages — MUST come before the * catch-all */}
      {import.meta.env.DEV && LazyMockupRoutes && (
        <Route
          path="/__mockups/*"
          element={
            <Suspense fallback={null}>
              <LazyMockupRoutes />
            </Suspense>
          }
        />
      )}

      {/* Privacy policy — public, no auth */}
      <Route
        path="/privacy"
        element={
          <PageSuspense>
            <LazyPrivacyPage />
          </PageSuspense>
        }
      />

      {/* Account deletion instructions — public, no auth (linked from stores + privacy policy) */}
      <Route
        path="/delete-account"
        element={
          <PageSuspense>
            <LazyDeleteAccountPage />
          </PageSuspense>
        }
      />

      {/* Support — public, no auth */}
      <Route
        path="/support"
        element={
          <PageSuspense>
            <LazySupportPage />
          </PageSuspense>
        }
      />

      {/* About — public, no auth */}
      <Route
        path="/about"
        element={
          <PageSuspense>
            <LazyAboutPage />
          </PageSuspense>
        }
      />

      {/* Landing page — lazy-loaded, outside auth */}
      <Route
        path="/"
        element={
          <Suspense fallback={null}>
            <LazyLandingPage />
          </Suspense>
        }
      />

      {/* Experiment landing pages — /lp/{slug} */}
      <Route
        path="/lp/:experimentName"
        element={
          <Suspense fallback={null}>
            <LazyExperimentLandingPage />
          </Suspense>
        }
      />

      {/* Tool pages — /tools/{slug} (formula-scaler, lash-map-builder, etc.) */}
      <Route
        path="/tools"
        element={
          <Suspense fallback={null}>
            <LazyToolsHubPage />
          </Suspense>
        }
      />
      <Route
        path="/tools/:toolSlug"
        element={
          <Suspense fallback={null}>
            <LazyToolPage />
          </Suspense>
        }
      />

      {/* SEO landing pages — /{slug} (salon-booking-software, hairstylist-booking-app, etc.) */}
      <Route
        path="/:seoSlug"
        element={
          <Suspense fallback={null}>
            <LazySeoLandingPage />
          </Suspense>
        }
      />

      {/*
       * Universal-link / App-link fallback routes.
       * Mobile apps intercept these URLs directly. On web (desktop/non-app)
       * we redirect authenticated users to the corresponding admin view.
       */}
      <Route
        path="/a/:appointmentId"
        element={
          <RequireAuth>
            <Navigate to="/admin/calendar" replace />
          </RequireAuth>
        }
      />
      <Route
        path="/c/:clientId"
        element={
          <RequireAuth>
            <NavigateToChat />
          </RequireAuth>
        }
      />

      <Route
        path="*"
        element={
          <PageSuspense>
            <LazyNotFoundPage />
          </PageSuspense>
        }
      />
    </Routes>
  );
}
